import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import AdminUserEditor from '../components/admin/AdminUserEditor';
import {
  UserPlus, Trash2, Shield, Loader2, Search, X,
  ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api.service';
import { USER_ROLES, USER_ROLE_LABELS, type UserRole } from '../types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  org_name: string;
  org_slug: string;
  plan: string;
  anonymization_exempt: boolean;
  created_at: string;
}

// Le type d'un compte se lit sur son RÔLE — c'est lui que pro.logfit.eu
// vérifie — pas sur la présence d'une organisation. Un participant attaché à
// une organisation (cas Clara's Box, 2026-09-04) s'affichait « Organisateur »
// ici et sur logfit.eu tout en étant refusé par le dashboard.
const isOrganizerRole = (u: { role?: string | null }) => u.role === 'organizer' || u.role === 'admin';

interface Event {
  id: string;
  name: string;
  date: string;
  status: string;
  org_name: string;
  org_slug: string;
}

/** Fiche détaillée renvoyée par GET /admin/users/{id}. */
interface UserDetail {
  user: User & {
    phone?: string | null;
    email_verified?: boolean;
    preferred_locale?: string | null;
    org_country?: string | null;
    org_token_balance?: number | null;
    org_contact_email?: string | null;
    organization_id?: string | null;
  };
  participations: Array<{
    event_id: string;
    event_name: string;
    date_start: string | null;
    event_status: string;
    org_name: string | null;
    category_name: string | null;
    team_name: string | null;
    registration_status: string;
    is_registration_owner: boolean;
  }>;
  volunteering: Array<{
    id: string;
    event_id: string;
    event_name: string;
    date_start: string | null;
    org_name: string | null;
    created_at: string;
  }>;
  staff_roles: Array<{
    id: string;
    event_id: string;
    event_name: string;
    date_start: string | null;
    org_name: string | null;
    role: string;
  }>;
  organized_events: Array<{
    id: string;
    name: string;
    date_start: string | null;
    status: string;
    visibility?: string | null;
  }>;
  subscription: {
    status: string;
    plan: string | null;
    expires_at: string | null;
  } | null;
}

const STAFF_ROLE_LABELS: Record<string, string> = {
  judge: 'Juge',
  head_judge: 'Head judge',
  race_director: 'Directeur de course',
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('fr-FR') : '-';

/** Ligne « libellé : valeur » d'une section de la fiche. */
const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between gap-3 border-b border-border/50 py-1">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right font-medium break-all">{value}</span>
  </div>
);

interface DetailListItem {
  key: string;
  main: string;
  meta?: string;
  badge?: string;
  badgeClass?: string;
  onClick?: () => void;
}

/** Collection de la fiche (participations, bénévolat, rôles juge…). */
const DetailList: React.FC<{
  items: DetailListItem[];
  emptyLabel: string;
  title?: string;
}> = ({ items, emptyLabel, title }) => (
  <div className="space-y-1">
    {title && <p className="text-xs text-muted-foreground pt-2">{title}</p>}
    {items.length === 0 ? (
      <p className="text-sm text-muted-foreground italic">{emptyLabel}</p>
    ) : (
      items.map(item => (
        <div
          key={item.key}
          onClick={item.onClick}
          className={cn(
            'flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2',
            item.onClick && 'cursor-pointer hover:border-primary/50'
          )}
        >
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{item.main}</p>
            {item.meta && (
              <p className="text-xs text-muted-foreground truncate">{item.meta}</p>
            )}
          </div>
          {item.badge && (
            <Badge variant="outline" className={cn('shrink-0', item.badgeClass)}>
              {item.badge}
            </Badge>
          )}
        </div>
      ))
    )}
  </div>
);

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState(false);
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState<'all' | 'organizers' | 'participants'>('all');

  // Recherche + tri de la table des utilisateurs
  type SortKey = 'email' | 'name' | 'type' | 'role' | 'org_name' | 'plan' | 'created_at';
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  // Fiche détaillée
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ columnKey }: { columnKey: SortKey }) => {
    if (sortKey !== columnKey) return <ArrowUpDown size={14} className="ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp size={14} className="ml-1" />
      : <ArrowDown size={14} className="ml-1" />;
  };

  const visibleUsers = React.useMemo(() => {
    let filtered = users.filter(u =>
      userFilter === 'all' ? true : userFilter === 'organizers' ? isOrganizerRole(u) : !isOrganizerRole(u)
    );

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(u =>
        [u.email, u.first_name, u.last_name, u.role, u.org_name, u.org_slug, u.plan]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(query)
      );
    }

    const valueOf = (u: User): string => {
      switch (sortKey) {
        case 'email': return u.email || '';
        case 'name': return `${u.first_name || ''} ${u.last_name || ''}`.trim();
        case 'type': return isOrganizerRole(u) ? 'Organisateur' : 'Participant';
        case 'role': return u.role || '';
        case 'org_name': return u.org_name || '';
        case 'plan': return u.plan || 'basic';
        case 'created_at': return u.created_at || '';
      }
    };

    return [...filtered].sort((a, b) => {
      // Les dates sont en ISO : l'ordre lexicographique est l'ordre chronologique,
      // pas besoin de les parser.
      const cmp = valueOf(a).localeCompare(valueOf(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [users, userFilter, searchQuery, sortKey, sortDir]);

  const openUserDetail = async (userId: string) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const res = await apiService.get(`/admin/users/${userId}`);
      setDetail(res.data);
    } catch (err: any) {
      setDetailError(err.response?.data?.detail || 'Impossible de charger la fiche');
    } finally {
      setDetailLoading(false);
    }
  };

  // Lien profond `?user=<id>` (notification « nouveau compte ») : la fiche
  // s'ouvre à l'arrivée, puis le paramètre est retiré de l'URL — sans quoi un
  // rechargement la rouvrirait après qu'on l'a fermée.
  const [searchParams, setSearchParams] = useSearchParams();
  const userParam = searchParams.get('user');
  useEffect(() => {
    if (!userParam) return;
    openUserDetail(userParam);
    const next = new URLSearchParams(searchParams);
    next.delete('user');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userParam]);

  // Form state for creation
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    // `participant` par défaut : c'est ce qu'est un compte neuf, et c'est le
    // seul rôle depuis lequel on peut ensuite devenir organisateur. Le défaut
    // était `user`, une valeur que plus rien ne reconnaît — le compte créé
    // n'était alors ni l'un ni l'autre, et restait bloqué là.
    role: 'participant' as UserRole,
    organization_name: ''
  });

  // Form state for editing

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, eventsRes] = await Promise.all([
        apiService.get('/admin/users'),
        apiService.getEvents()
      ]);

      setUsers(usersRes.data.users || []);
      setEvents(eventsRes.data || []);
    } catch (error: any) {
      console.error('Erreur chargement données admin:', error);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      setError(null);
      await apiService.post('/admin/users', newUser);

      setSuccess('Utilisateur créé avec succès');
      setCreateDialogOpen(false);
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'participant',
        organization_name: ''
      });

      loadData();

      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  // Écriture acceptée depuis le panneau : la liste et la fiche suivent, sans
  // recharger (recharger refermerait ce que l'admin est en train d'éditer).
  const applyUserPatch = (userId: string, patch: Partial<User>) => {
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, ...patch } : u)));
    setDetail((prev) => (prev && prev.user.id === userId ? { ...prev, user: { ...prev.user, ...patch } } : prev));
  };

  const handleDeleteUser = (userId: string) => setUserToDelete(userId);

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    setDeletingUser(true);
    try {
      setError(null);
      await apiService.delete(`/admin/users/${userToDelete}`);
      setUserToDelete(null);
      setSuccess('Utilisateur supprimé avec succès');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleEventClick = (eventId: string) => {
    navigate(`/events/${eventId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <Shield className="h-8 w-8 md:h-10 md:w-10 text-muted-foreground" />
          <h1 className="text-xl md:text-2xl font-bold">Dashboard Admin</h1>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="w-full sm:w-auto">
          <UserPlus className="h-4 w-4 mr-2" />
          Créer un utilisateur
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="text-sm underline ml-4">Fermer</button>
          </AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert variant="success">
          <AlertDescription className="flex items-center justify-between">
            {success}
            <button onClick={() => setSuccess(null)} className="text-sm underline ml-4">Fermer</button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-primary">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Utilisateurs</p>
            <p className="text-4xl font-bold mt-2">{users.length}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {users.filter(u => u.role === 'admin').length} admin(s)
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Organisateurs</p>
            <p className="text-4xl font-bold mt-2">{users.filter(isOrganizerRole).length}</p>
            <p className="text-sm text-muted-foreground mt-1">Avec organisation</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Participants</p>
            <p className="text-4xl font-bold mt-2">{users.filter(u => !isOrganizerRole(u)).length}</p>
            <p className="text-sm text-muted-foreground mt-1">Sans organisation</p>
          </CardContent>
        </Card>
        <Card className="border-primary">
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Événements</p>
            <p className="text-4xl font-bold mt-2">{events.length}</p>
            <p className="text-sm text-muted-foreground mt-1">Toutes organisations</p>
          </CardContent>
        </Card>
      </div>

      {/* Users Filter + Table */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center gap-3">
          <CardTitle className="text-foreground">Gestion des Utilisateurs</CardTitle>

          {/* Recherche déployable (même grammaire que la table des équipes) */}
          <div
            className={cn(
              'flex h-7 items-center rounded-md border border-border bg-background transition-[width] duration-200 ease-out',
              searchExpanded || searchQuery ? 'w-full sm:w-64' : 'w-8'
            )}
          >
            <button
              type="button"
              onClick={() => {
                if (searchExpanded) {
                  setSearchExpanded(false);
                  setSearchQuery('');
                } else {
                  setSearchExpanded(true);
                  setTimeout(() => searchInputRef.current?.focus(), 50);
                }
              }}
              className="flex h-full w-8 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Rechercher un utilisateur"
            >
              <Search size={13} />
            </button>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setSearchQuery('');
                  setSearchExpanded(false);
                }
              }}
              onBlur={() => { if (!searchQuery) setSearchExpanded(false); }}
              placeholder="Nom, email, organisation…"
              className="h-full w-full min-w-0 bg-transparent pr-1 text-xs font-semibold text-foreground outline-none placeholder:font-normal placeholder:text-muted-foreground"
              tabIndex={searchExpanded ? 0 : -1}
            />
            {searchQuery && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                className="flex h-full w-6 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Effacer la recherche"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <div className="flex gap-1 sm:ml-auto text-xs">
            <Button variant={userFilter === 'all' ? 'default' : 'outline'} size="sm" className="h-7 px-2 text-xs" onClick={() => setUserFilter('all')}>
              Tous ({users.length})
            </Button>
            <Button variant={userFilter === 'organizers' ? 'default' : 'outline'} size="sm" className="h-7 px-2 text-xs" onClick={() => setUserFilter('organizers')}>
              <span className="hidden sm:inline">Organisateurs</span><span className="sm:hidden">Orga</span> ({users.filter(isOrganizerRole).length})
            </Button>
            <Button variant={userFilter === 'participants' ? 'default' : 'outline'} size="sm" className="h-7 px-2 text-xs" onClick={() => setUserFilter('participants')}>
              <span className="hidden sm:inline">Participants</span><span className="sm:hidden">Part.</span> ({users.filter(u => !isOrganizerRole(u)).length})
            </Button>
          </div>
        </CardHeader>

        {/* Desktop Table */}
        <CardContent className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                {([
                  ['email', 'Email'],
                  ['name', 'Nom'],
                  ['type', 'Type'],
                  ['role', 'Rôle'],
                  ['org_name', 'Organisation'],
                  ['plan', 'Plan'],
                  ['created_at', 'Inscrit le'],
                ] as [SortKey, string][]).map(([key, label]) => (
                  <TableHead
                    key={key}
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort(key)}
                  >
                    <span className="flex items-center">{label}<SortIcon columnKey={key} /></span>
                  </TableHead>
                ))}
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Aucun utilisateur ne correspond à la recherche
                  </TableCell>
                </TableRow>
              )}
              {visibleUsers.map((u) => (
                <TableRow
                  key={u.id}
                  onClick={() => openUserDetail(u.id)}
                  className="cursor-pointer"
                  title="Voir la fiche détaillée"
                >
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{u.email}</span>
                      {/* Visible dans la liste (et pas seulement dans le dialog) :
                          une exemption laisse de la PII réelle sur staging, elle
                          doit s'auditer d'un coup d'œil. */}
                      {u.anonymization_exempt && (
                        <Badge
                          variant="outline"
                          className="border-warning/30 bg-warning/20 text-warning"
                          title="Exempt d'anonymisation : email et mot de passe réels préservés sur staging"
                        >
                          Exempt
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{u.first_name} {u.last_name}</TableCell>
                  <TableCell>
                    {isOrganizerRole(u) ? (
                      <Badge className="bg-white/[0.06] text-foreground border-white/20">Organisateur</Badge>
                    ) : (
                      <Badge variant="outline">Participant</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                      {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>{u.org_name || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={u.plan === 'premium' ? 'default' : 'secondary'}
                        className={u.plan === 'premium' ? 'bg-primary/20 text-primary' : ''}
                      >
                        {u.plan || 'basic'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                    <TableCell>
                      {/* stopPropagation : la ligne ouvre la fiche, les actions
                          ne doivent pas la déclencher au passage. */}
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(u.id)}
                          disabled={u.id === user?.id}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>

        {/* Mobile Cards */}
        <CardContent className="md:hidden space-y-3">
          {visibleUsers.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-6">
              Aucun utilisateur ne correspond à la recherche
            </p>
          )}
          {visibleUsers.map((u) => (
            <Card key={u.id} onClick={() => openUserDetail(u.id)} className="cursor-pointer">
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-semibold">{u.first_name} {u.last_name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(u.id)}
                        disabled={u.id === user?.id}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    {isOrganizerRole(u) ? (
                      <Badge className="bg-white/[0.06] text-foreground border-white/20">Organisateur</Badge>
                    ) : (
                      <Badge variant="outline">Participant</Badge>
                    )}
                    <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                      {u.role}
                    </Badge>
                    <Badge variant={u.plan === 'premium' ? 'default' : 'secondary'}
                      className={u.plan === 'premium' ? 'bg-primary/20 text-primary' : ''}
                    >
                      {u.plan || 'basic'}
                    </Badge>
                    {u.org_name && <span className="text-muted-foreground">{u.org_name}</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Events Table - Desktop */}
      <div className="hidden md:block">
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">Tous les Événements</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.slice(0, 10).map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>{event.name}</TableCell>
                    <TableCell>{event.date}</TableCell>
                    <TableCell>
                      <Badge variant={event.status === 'active' ? 'default' : 'secondary'}
                        className={event.status === 'active' ? 'bg-success/20 text-success' : ''}
                      >
                        {event.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{event.org_name || '-'}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEventClick(event.id)}
                      >
                        Ouvrir
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Events Cards - Mobile */}
      <div className="md:hidden">
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">Tous les Événements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {events.slice(0, 10).map((event) => (
              <Card key={event.id}>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="font-semibold">{event.name}</p>
                        <p className="text-sm text-muted-foreground">{event.date}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEventClick(event.id)}
                        className="shrink-0"
                      >
                        Ouvrir
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Badge variant={event.status === 'active' ? 'default' : 'secondary'}
                        className={event.status === 'active' ? 'bg-success/20 text-success' : ''}
                      >
                        {event.status}
                      </Badge>
                      <span className="text-muted-foreground">{event.org_name || '-'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Fiche utilisateur : panneau latéral, la grammaire de la page Équipes.
          Une fiche à cinq sections se lit en colonne ; en fenêtre centrée, la
          grille débordait à droite (constat de Bryan, 2026-09-27). */}
      <Sheet open={detailOpen} onOpenChange={(open) => {
        setDetailOpen(open);
        if (!open) { setDetail(null); setDetailError(null); }
      }}>
        <SheetContent
          side="right"
          hideClose
          className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[560px]"
        >
          <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <SheetDescription className="text-xs">
                {detail
                  ? [detail.user.email, USER_ROLE_LABELS[detail.user.role as UserRole] ?? detail.user.role]
                      .filter(Boolean).join(' · ')
                  : 'Fiche utilisateur'}
              </SheetDescription>
              <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">
                {detail ? `${detail.user.first_name} ${detail.user.last_name}` : 'Fiche utilisateur'}
              </SheetTitle>
            </div>
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label="Fermer" onClick={() => setDetailOpen(false)}>
              <X size={16} />
            </Button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6">

          {detailLoading && (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {detailError && (
            <Alert variant="destructive">
              <AlertDescription>{detailError}</AlertDescription>
            </Alert>
          )}

          {detail && !detailLoading && (
            <div className="space-y-7">
              {/* Infos perso */}
              <section className="space-y-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Informations
                </h3>
                <AdminUserEditor
                  user={detail.user}
                  onSaved={(patch) => applyUserPatch(detail.user.id, patch as Partial<User>)}
                  onRoleChanged={() => { void openUserDetail(detail.user.id); void loadData(); }}
                />
                <div className="grid grid-cols-1 gap-y-1.5 text-sm">
                  <DetailRow label="Téléphone" value={detail.user.phone || '-'} />
                  <DetailRow
                    label="Type"
                    value={isOrganizerRole(detail.user) ? 'Organisateur' : 'Participant'}
                  />
                  <DetailRow label="Inscrit le" value={formatDate(detail.user.created_at)} />
                  <DetailRow
                    label="Email vérifié"
                    value={detail.user.email_verified ? 'Oui' : 'Non'}
                  />
                  <DetailRow label="Langue" value={detail.user.preferred_locale || '-'} />
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {detail.subscription && detail.subscription.status !== 'free' && (
                    <Badge className="bg-primary/20 text-primary border-primary/30">
                      Premium athlète : {detail.subscription.plan || detail.subscription.status}
                      {detail.subscription.expires_at
                        ? ` (jusqu'au ${formatDate(detail.subscription.expires_at)})`
                        : ''}
                    </Badge>
                  )}
                </div>
              </section>

              {/* Organisation */}
              {detail.user.org_name && (
                <section className="space-y-2">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    Organisation
                  </h3>
                  <div className="grid grid-cols-1 gap-y-1.5 text-sm">
                    <DetailRow
                      label="Jetons"
                      value={String(detail.user.org_token_balance ?? 0)}
                    />
                  </div>
                  <DetailList
                    title={`Événements organisés (${detail.organized_events.length})`}
                    emptyLabel="Aucun événement"
                    items={detail.organized_events.map(e => ({
                      key: e.id,
                      main: e.name,
                      meta: [formatDate(e.date_start), e.status, e.visibility]
                        .filter(Boolean).join(' · '),
                      onClick: () => { setDetailOpen(false); navigate(`/admin/events/${e.id}/settings`); },
                    }))}
                  />
                </section>
              )}

              {/* Participations athlète */}
              <section className="space-y-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Participations ({detail.participations.length})
                </h3>
                <DetailList
                  emptyLabel="Aucune inscription"
                  items={detail.participations.map(p => ({
                    key: `${p.registration_status}-${p.event_id}-${p.team_name}`,
                    main: p.event_name,
                    meta: [
                      formatDate(p.date_start),
                      p.category_name,
                      p.team_name ? `Équipe ${p.team_name}` : null,
                      p.org_name,
                      p.is_registration_owner ? 'Inscription à son nom' : null,
                    ].filter(Boolean).join(' · '),
                    badge: p.registration_status,
                    badgeClass: p.registration_status === 'confirmed'
                      ? 'bg-success/20 text-success border-success/30'
                      : undefined,
                  }))}
                />
              </section>

              {/* Bénévolat */}
              <section className="space-y-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Bénévole ({detail.volunteering.length})
                </h3>
                <DetailList
                  emptyLabel="Aucune inscription bénévole"
                  items={detail.volunteering.map(v => ({
                    key: v.id,
                    main: v.event_name,
                    meta: [formatDate(v.date_start), v.org_name, `inscrit le ${formatDate(v.created_at)}`]
                      .filter(Boolean).join(' · '),
                  }))}
                />
              </section>

              {/* Staff / juge */}
              <section className="space-y-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Juge / staff ({detail.staff_roles.length})
                </h3>
                <DetailList
                  emptyLabel="Aucun rôle juge"
                  items={detail.staff_roles.map(s => ({
                    key: s.id,
                    main: s.event_name,
                    meta: [formatDate(s.date_start), s.org_name].filter(Boolean).join(' · '),
                    badge: STAFF_ROLE_LABELS[s.role] || s.role,
                  }))}
                />
              </section>
            </div>
          )}

          </div>

          <div className="flex items-center justify-end gap-2 border-t border-white/[0.08] px-6 py-4">
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Fermer</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-foreground">Créer un Utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="off"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="first_name">Prénom</Label>
              <Input
                id="first_name"
                value={newUser.first_name}
                onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Nom</Label>
              <Input
                id="last_name"
                value={newUser.last_name}
                onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="organization">Organisation</Label>
              <Input
                id="organization"
                value={newUser.organization_name}
                onChange={(e) => setNewUser({ ...newUser, organization_name: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Nom de l'organisation (sera créée si elle n'existe pas)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select
                value={newUser.role}
                onValueChange={(value) => setNewUser({ ...newUser, role: value as UserRole })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map((role) => (
                    <SelectItem key={role} value={role}>{USER_ROLE_LABELS[role]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateUser}>
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={userToDelete !== null}
        onOpenChange={(o) => { if (!o) setUserToDelete(null); }}
        title={t('admin.users.deleteTitle')}
        message={t('admin.users.deleteConfirm')}
        confirmLabel={t('common.delete')}
        destructive
        loading={deletingUser}
        onConfirm={confirmDeleteUser}
      />
    </div>
  );
};

export default AdminDashboard;
